// The product's single operator-facing state, derived from the registry row.
//
// Lives apart from `ProductCard.tsx` so that file exports components only and
// keeps React Fast Refresh — the same reason `tone.ts` and `navConfig.ts` are
// separate modules.

import type { ProductRow } from "../controlPlane/api";
import type { Tone } from "./tone";

/** The product's single operator-facing state.
 *
 *  Order matters and is not alphabetical: `disabled` wins over everything
 *  because a disabled product is not serving whatever its pointer says, and
 *  `never published` beats `draft pending` because a draft on a product with
 *  no live revision is not "pending" against anything. */
export function productState(p: ProductRow): { label: string; tone: Tone } {
  // Registry semantics first: an entry that is not a current standalone
  // product has no serving state to report. The label is the server's kind or
  // status, verbatim — this console classifies nothing itself.
  if (p.kind !== "product") return { label: p.kind.toUpperCase(), tone: "neutral" };
  if (p.status !== "current") return { label: p.status.toUpperCase(), tone: "neutral" };
  if (!p.enabled) return { label: "DISABLED", tone: "danger" };
  if (p.currentRevisionId === null) return { label: "NEVER PUBLISHED", tone: "warn" };
  if (p.hasUnpublishedDraft) return { label: "DRAFT PENDING", tone: "warn" };
  return { label: "LIVE", tone: "ok" };
}
